import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, BookOpenIcon } from 'lucide-react';
import { books, Book, CURRENCY_NOTE, formatPrice, lowestPrice } from '../data/books';
import { SectionHeading } from './SectionHeading';
export function Catalog() {
  const [selected, setSelected] = useState<Book | null>(null);
  return <section id="catalog" className="w-full bg-paper py-28" data-id="element-35" data-mp="1150z9caixjag">
      <div className="mx-auto max-w-6xl px-6" data-id="element-36" data-mp="113g71tbomcfr">
        <SectionHeading eyebrow="Our Catalog" title="Three books in print" description="Each title is a considered work — from ancient beauty to the mysteries of the soul." data-id="element-37" data-mp="15vhen1xwnezg" />

        <p className="mt-6 text-center text-ink/55 text-sm max-w-2xl mx-auto" data-id="element-38" data-mp="x2w58cg5ykna">
          {CURRENCY_NOTE}
        </p>

        <div className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-39" data-mp="i76lkkygajlq">
          {books.map((book, i) => <motion.article key={book.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="group flex flex-col" data-id="element-40" data-mp="n4q8sad4m6pt" data-mpi={i}>
              <button onClick={() => setSelected(book)} className="relative block overflow-hidden bg-ink/5 shadow-lg shadow-black/10" aria-label={`View details for ${book.title}`} data-id="element-41" data-mp="2eemisxlna1sd">
                <img src={book.cover} alt={`${book.title} cover`} className="w-full aspect-[2/3] object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-42" data-mp="1hrhky2yph9q0" />
                <span className="absolute inset-0 flex items-center justify-center bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-43" data-mp="1qq35u4rnh0ty">
                  <span className="flex items-center gap-2 text-paper text-sm tracking-wide" data-id="element-44" data-mp="z0qaey2zfda1">
                    <BookOpenIcon size={16} data-id="element-45" data-mp="2y5i3xjvu3cb1" /> View details &amp; pricing
                  </span>
                </span>
                <span className="absolute top-3 right-3 bg-paper/95 px-3 py-1 text-xs text-ink tracking-wide" data-id="element-46" data-mp="33302cgc8694s">
                  From {formatPrice(lowestPrice(book), book.currency)}
                </span>
              </button>

              <div className="mt-6 flex-1" data-id="element-47" data-mp="3ct32d0hrkncm">
                <p className="text-gold text-xs uppercase tracking-[0.3em] mb-2" data-id="element-48" data-mp="2f6m8k588orvm">
                  {book.genre}
                </p>
                <h3 className="font-serif text-2xl text-ink leading-tight" data-id="element-49" data-mp="2k5yvsr30w0yq">
                  {book.title}
                </h3>
                <p className="text-ink/55 text-sm mt-1" data-id="element-50" data-mp="2p6d3u94mjawr">
                  by {book.author} · {book.year}
                </p>

                <ul className="mt-4 space-y-1.5 border-t border-ink/10 pt-4" data-id="element-51" data-mp="4ebaxu1a9ywa">
                  {book.editions.map(edition => <li key={edition.name} className="flex justify-between text-sm" data-id="element-52" data-mp="9c338p5ubn10">
                      <span className="text-ink/60" data-id="element-53" data-mp="1twzqu8ixjcgp">{edition.name}</span>
                      <span className="text-ink font-medium" data-id="element-54" data-mp="1owvkvcfne04q">
                        {formatPrice(edition.price, book.currency)}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.article>)}
        </div>
      </div>

      <AnimatePresence data-id="element-55" data-mp="1a8iju11pdr3d">
        {selected && <BookModal book={selected} onClose={() => setSelected(null)} data-id="element-56" data-mp="165cjfa19uu4t" />}
      </AnimatePresence>
    </section>;
}
function BookModal({
  book,
  onClose
}: {
  book: Book;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} onClick={onClose} className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label={`${book.title} details`} data-id="element-57" data-mp="w8g6ms4cblug">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl bg-paper max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-58" data-mp="1o8t0wwnc3l9r">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-ink/60 hover:text-ink bg-paper/80 rounded-full p-1" aria-label="Close" data-id="element-59" data-mp="14jsdjcw3vjki">
          <XIcon size={22} data-id="element-60" data-mp="vjvv3t0b7gxw" />
        </button>

        <div className="grid sm:grid-cols-[240px_1fr]" data-id="element-61" data-mp="vkx4zu5l9xe1">
          <div className="bg-ink p-8 flex items-center justify-center" data-id="element-62" data-mp="10hxekf1ndi9w">
            <img src={book.cover} alt={`${book.title} cover`} className="w-44 shadow-xl shadow-black/50" data-id="element-63" data-mp="ll6cfy0l9sgl" />
          </div>
          <div className="p-8 sm:p-10" data-id="element-64" data-mp="qjhp0gi9qh2i">
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3" data-id="element-65" data-mp="6xxvegklmswp">
              {book.genre}
            </p>
            <h3 className="font-serif text-3xl text-ink leading-tight" data-id="element-66" data-mp="2xxix1h28rsa">
              {book.title}
            </h3>
            <p className="font-serif italic text-ink/60 text-lg mt-1" data-id="element-67" data-mp="gxggx1g5qmqv">
              {book.subtitle}
            </p>
            <p className="text-ink/55 text-sm mt-3" data-id="element-68" data-mp="bw95mset35fg">by {book.author}</p>

            <p className="text-ink/75 leading-relaxed mt-6" data-id="element-69" data-mp="pw0r1e7njo3h">{book.description}</p>

            <div className="mt-8 border-t border-ink/10 pt-6" data-id="element-70" data-mp="lwji2at0o8tk">
              <h4 className="text-ink/50 text-xs uppercase tracking-[0.3em] mb-4" data-id="element-71" data-mp="zn558bpi74x9">
                Price
              </h4>
              <ul className="space-y-2" data-id="element-72" data-mp="unjvd68spc5r">
                {book.editions.map(edition => <li key={edition.name} className="flex justify-between items-baseline" data-id="element-73" data-mp="19kt0r3uxoggh">
                    <span className="text-ink/70 text-sm" data-id="element-74" data-mp="14o2lgiqydow4">{edition.name}</span>
                    <span className="font-serif text-2xl text-ink" data-id="element-75" data-mp="1bqqz1mhdwiky">
                      {formatPrice(edition.price, book.currency)}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-4 text-ink/50 text-xs leading-relaxed" data-id="element-76" data-mp="1lmat03ce1772">
                {CURRENCY_NOTE}
              </p>
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-y-3 text-sm border-t border-ink/10 pt-6" data-id="element-77" data-mp="1vdoz4tyuh596">
              <dt className="text-ink/50" data-id="element-78" data-mp="1zf0jajyy8y9z">Format</dt>
              <dd className="text-ink text-right" data-id="element-79" data-mp="1zdfkgi5lqt6b">{book.format}</dd>
              <dt className="text-ink/50" data-id="element-80" data-mp="24dtuu6uoef7i">Pages</dt>
              <dd className="text-ink text-right" data-id="element-81" data-mp="24c0l2iorneva">{book.pages}</dd>
              <dt className="text-ink/50" data-id="element-82" data-mp="29bveasw0kgji">Published</dt>
              <dd className="text-ink text-right" data-id="element-83" data-mp="a02g4sxhp557">{book.year}</dd>
              <dt className="text-ink/50" data-id="element-84" data-mp="a1wybzhdmj5l">ISBN</dt>
              <dd className="text-ink text-right" data-id="element-85" data-mp="eynh4ukkfsup">{book.isbn}</dd>
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-86" data-mp="7fe50tkodg1y">
              <a href={book.purchaseUrl ?? '#contact'} onClick={book.purchaseUrl ? undefined : onClose} target={book.purchaseUrl ? '_blank' : undefined} rel={book.purchaseUrl ? 'noreferrer' : undefined} className="inline-block px-7 py-3 bg-ink text-paper text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors" data-id="element-87" data-mp="7hqurswocupk">
                {book.purchaseUrl ? book.purchaseLabel ?? 'Buy this title' : 'Inquire about this title'}
              </a>
              <a href="#policies" onClick={onClose} className="text-sm text-ink/60 hover:text-gold transition-colors underline underline-offset-4" data-id="element-88" data-mp="gerh7u06oiqd">
                Refund &amp; shipping policy
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}